import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, MailOpen, Mail } from "lucide-react";
import { fmtDateTime } from "../../lib/format";
import { adminListMessages, adminMarkMessageRead, adminDeleteMessage } from "../../lib/api";

export function InboxTab({ onMutate }) {
  const [messages, setMessages] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const load = () =>
    adminListMessages().then(setMessages).catch(() => toast.error("Could not load messages"));

  useEffect(() => { load(); }, []);

  const toggleRead = async (m) => {
    try {
      await adminMarkMessageRead(m.id, !m.read);
      await load();
      onMutate?.();
    } catch {
      toast.error("Could not update message");
    }
  };

  const remove = async (m) => {
    try {
      await adminDeleteMessage(m.id);
      toast.success("Message deleted");
      setConfirmDeleteId(null);
      await load();
      onMutate?.();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-inbox-tab">
      <p className="font-mono text-xs text-dim mb-4">Messages sent through the contact form on the home page.</p>
      {messages === null ? (
        <p className="font-mono text-xs text-dim">loading ...</p>
      ) : messages.length === 0 ? (
        <div className="p-6 rounded bg-panelalt border border-white/5 font-mono text-xs" data-testid="admin-inbox-empty">
          <p className="text-cyber mb-1">$ ls ~/inbox</p>
          <p className="text-dim">&gt; 0 messages. New contact-form notes appear here.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-inbox-list">
          {messages.map((m) => (
            <div key={m.id} className={`p-4 rounded border ${m.read ? "bg-panelalt border-white/5" : "bg-panel border-cyber/40"}`} data-testid={`message-row-${m.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  {!m.read && <span className="w-2 h-2 rounded-full bg-cyber shrink-0" title="Unread"></span>}
                  <span className="font-mono text-xs text-white font-bold truncate">{m.name}</span>
                  <span className="font-mono text-[11px] text-cyber truncate">{m.contact}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-mono text-[10px] text-dim">{fmtDateTime(m.created_at)}</span>
                  <button type="button" onClick={() => toggleRead(m)} aria-label={m.read ? "Mark unread" : "Mark read"}
                    className="p-1.5 rounded bg-white/5 text-dim hover:text-cyber transition-colors"
                    data-testid={`message-toggle-read-${m.id}`}>
                    {m.read ? <Mail className="w-3.5 h-3.5" /> : <MailOpen className="w-3.5 h-3.5" />}
                  </button>
                  {confirmDeleteId === m.id ? (
                    <button type="button" onClick={() => remove(m)}
                      className="px-2 py-1 rounded bg-red-500/20 text-red-400 font-mono text-[10px] font-bold hover:bg-red-500/30 transition-colors"
                      data-testid={`message-confirm-delete-${m.id}`}>
                      Confirm?
                    </button>
                  ) : (
                    <button type="button" onClick={() => setConfirmDeleteId(m.id)} aria-label="Delete message"
                      className="p-1.5 rounded bg-white/5 text-dim hover:text-red-400 transition-colors"
                      data-testid={`message-delete-${m.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <p className="text-xs text-[#cbd5e1] leading-relaxed whitespace-pre-wrap">{m.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
